import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Form, Row, Col, Button } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import FormContainer from '../../components/FormContainer';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import {
  useGetUserDetailsQuery,
  useUpdateUserMutation,
  useGetManagersQuery,
} from '../../slices/usersApiSlice';

import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice.js';

import {
  CATEGORY_OF_STAFF,
  REASON_FOR_REQUEST,
  SHIFT_STATUS,
  LINE_STATUS,
} from '../../utils/enums';

const dropdownStyle = {
  backgroundColor: '#F5F5F5',
};
const textStyle = {
  backgroundColor: '#f5f5dc',
};

const UserEditScreen = () => {
  const [availableContractTypes, setAvailableContractTypes] = useState([]);

  const { id: userId } = useParams();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLocum, setIsLocum] = useState(false);
  const [isBank, setIsBank] = useState(false);
  const [isStudentNurse, setIsStudentNurse] = useState(false);
  const [locumEndDate, setLocumEndDate] = useState(new Date());

  const [isApproved, setIsApproved] = useState(false);
  const [isManager, setIsManager] = useState(false);

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');

  const [employeenumber, setEmployeenumber] = useState('');
  const [jobtitle, setJobtitle] = useState('');

  const [speciality, setSpeciality] = useState('');
  const [specialityID, setSpecialityID] = useState('');
  const [directoratedepartment, setDirectoratedepartment] = useState('');
  const [directorateID, setDirectorateID] = useState('');

  const [comment, setComment] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date());
  const [reviewDate, setReviewDate] = useState(new Date());
  const [salutation, setSalutation] = useState('--');

  const [openingBalance, setOpeningBalance] = useState(0);
  const [bringForward, setBringForward] = useState(0);

  // Ward exclusions
  const [wardExclusions, setWardExclusions] = useState([]);

  const {
    data: hospitalLocations,
    isLoading: isLoadingHL,
    error: errorHL,
    refetch: refetchHL,
  } = useGetHospitalLocationsQuery();

  const {
    data: managers,
    refetch: refetchM,
    isLoading: isLoadingM,
    errorM,
  } = useGetManagersQuery();

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useGetUserDetailsQuery(userId);

  const [updateUser, { isLoading: loadingUpdate }] = useUpdateUserMutation();

  const navigate = useNavigate();

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await updateUser({
        userId,
        name,
        email,
        isAdmin,
        isLocum,
        isBank,
        isStudentNurse,
        locumEndDate,
        isApproved,
        isManager,
        employeenumber,
        firstname,
        lastname,
        jobtitle,
        speciality,
        specialityID,
        directoratedepartment,
        directorateID,
        effectiveDate,
        reviewDate,
        comment,
        salutation,
        openingBalance,
        bringForward,
        wardExclusions,
      }).unwrap();
      toast.success('User updated successfully');
      refetch();
      navigate('/admin/userlist');
    } catch (err) {
      toast.error(err?.data?.message || err?.error || err?.message);
    }
  };

  const contractData = {
    'Public Only Consultant Contract': [
      'POCC Clinician',
      'POCC Academic Professor',
      'POCC Associate Professor',
      'POCC Senior Lecturer',
    ],
    '2008 Contract': [
      'Type A',
      'Type B',
      'Type B Academic',
      'Type B*',
      'Type B* Academic',
      'Type C',
      'Type C Academic',
    ],
    'Buckley Contract': [
      'Category I',
      'Category II',
      'Category II Senior Lecturer',
    ],
  };

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setIsAdmin(user.isAdmin);
      setIsLocum(user.isLocum);
      setIsBank(user.isBank);
      setIsStudentNurse(user.isStudentNurse || false);

      const locumEndDate1 = new Date(user.locumEndDate);
      let locumEndDate2 = '';
      if (
        locumEndDate1 &&
        locumEndDate1 instanceof Date &&
        !isNaN(locumEndDate1.getTime())
      ) {
        locumEndDate2 = locumEndDate1.toISOString().slice(0, 10);
      } else {
        locumEndDate2 = '';
      }
      setLocumEndDate(locumEndDate2);

      setIsApproved(user.isApproved);
      setIsManager(user.isManager);

      setFirstname(user.firstname);
      setLastname(user.lastname);

      setEmployeenumber(user.employeenumber);
      setJobtitle(user.jobtitle);

      setSpeciality(user.speciality);
      setSpecialityID(user.specialityID);
      setDirectoratedepartment(user.directoratedepartment);
      setDirectorateID(user.directorateID);

      setComment(user.comment);
      setSalutation(user.salutation);

      setOpeningBalance(user.openingBalance || 0);
      setBringForward(user.bringForward || 0);

      setWardExclusions(user.wardExclusions || []);

      const reviewDate1 = new Date(user.reviewDate);
      let reviewDate2 = '';
      if (
        reviewDate1 &&
        reviewDate1 instanceof Date &&
        !isNaN(reviewDate1.getTime())
      ) {
        reviewDate2 = reviewDate1.toISOString().slice(0, 10);
      } else {
        reviewDate2 = '';
      }

      const effectiveDate1 = new Date(user.effectiveDate);
      let effectiveDate2 = '';
      if (
        effectiveDate1 &&
        effectiveDate1 instanceof Date &&
        !isNaN(effectiveDate1.getTime())
      ) {
        effectiveDate2 = effectiveDate1.toISOString().slice(0, 10);
      } else {
        effectiveDate2 = '';
      }

      setReviewDate(reviewDate2);
      setEffectiveDate(effectiveDate2);
    }
  }, [user]);

  const getLocationsByDirectorate = () => {
    if (!hospitalLocations) return {};
    return hospitalLocations.reduce((acc, location) => {
      if (!acc[location.directorate]) {
        acc[location.directorate] = [];
      }
      acc[location.directorate].push(location);
      return acc;
    }, {});
  };

  const locationsByDirectorate = getLocationsByDirectorate();
  const directorates = Object.keys(locationsByDirectorate).sort();

  const currentLocation = hospitalLocations?.find(
    (loc) => loc._id === specialityID
  );

  return (
    <>
      <Link to='/admin/userlist' className='btn btn-light my-3'>
        Go Back
      </Link>
      <Container fluid>
        <h1>Edit Employee Profile...</h1>
        {loadingUpdate && <Loader />}
        {isLoading || isLoadingHL || isLoadingM ? (
          <Loader />
        ) : error ? (
          <Message variant='danger'>
            {error?.data?.message || error.error}
          </Message>
        ) : errorHL ? (
          <Message variant='danger'>
            {errorHL?.data?.message ||
              errorHL?.error ||
              'Error loading hospital locations'}
          </Message>
        ) : (
          <Form onSubmit={submitHandler}>
            <Row>
              {/* ── First Column ── */}
              <Col md={6}>
                <div
                  style={{
                    border: '1px solid #ddd',
                    padding: '10px',
                    borderRadius: '10px',
                    backgroundColor: '#F0F8FF',
                  }}
                >
                  <Row>
                    <Col sm={3}>
                      <Form.Group controlId='salutation'>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          as='select'
                          value={salutation}
                          onChange={(e) => setSalutation(e.target.value)}
                          style={textStyle}
                        >
                          <option value=''>Select Title</option>
                          <option value='Cllr.'>Cllr.</option>
                          <option value='Mr.'>Mr.</option>
                          <option value='Ms.'>Ms.</option>
                          <option value='Mrs'>Mrs</option>
                          <option value='Miss'>Miss</option>
                          <option value='Dr.'>Dr.</option>
                          <option value='Professor'>Professor</option>
                          <option value='Mx'>Mx</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>

                    <Col sm={4}>
                      <Form.Group controlId='firstname'>
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type='text'
                          placeholder='Enter First Name'
                          value={firstname}
                          onChange={(e) => setFirstname(e.target.value)}
                          style={textStyle}
                        />
                      </Form.Group>
                    </Col>

                    <Col sm={5}>
                      <Form.Group controlId='lastname'>
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type='text'
                          placeholder='Enter Last Name'
                          value={lastname}
                          onChange={(e) => setLastname(e.target.value)}
                          style={textStyle}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group controlId='email'>
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type='email'
                      placeholder='Enter Email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={textStyle}
                    />
                  </Form.Group>

                  <Row>
                    <Col sm={6}>
                      <Form.Group controlId='employeenumber'>
                        <Form.Label>Employee Number</Form.Label>
                        <Form.Control
                          type='text'
                          placeholder='Enter Employee Number'
                          value={employeenumber}
                          onChange={(e) => setEmployeenumber(e.target.value)}
                          style={textStyle}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group controlId='jobtitle'>
                        <Form.Label>Job Title / Category of Staff</Form.Label>
                        <Form.Control
                          as='select'
                          value={jobtitle}
                          onChange={(e) => setJobtitle(e.target.value)}
                          style={textStyle}
                        >
                          <option value=''>Select Job Title</option>
                          {CATEGORY_OF_STAFF.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm={6}>
                      <Form.Group controlId='effectiveDate'>
                        <Form.Label>Effective Date</Form.Label>
                        <Form.Control
                          type='date'
                          value={effectiveDate}
                          onChange={(e) => setEffectiveDate(e.target.value)}
                          style={textStyle}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group controlId='reviewDate'>
                        <Form.Label>Review Date</Form.Label>
                        <Form.Control
                          type='date'
                          placeholder='Review Date'
                          value={reviewDate}
                          onChange={(e) => setReviewDate(e.target.value)}
                          style={textStyle}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div>
                  <p></p>
                </div>

                {/* ── Allow Login / Flags box ── */}
                <div
                  style={{
                    border: '1px solid #ccc',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    backgroundColor: '#F0F8FF',
                  }}
                >
                  <Form.Group className='my-2' controlId='isApproved'>
                    <Form.Label>Allow login</Form.Label>

                    <Form.Check
                      type='checkbox'
                      label='Approved to log in'
                      checked={isApproved}
                      onChange={(e) => setIsApproved(e.target.checked)}
                      style={textStyle}
                    />

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                      }}
                    >
                      <Form.Check
                        type='checkbox'
                        label='Bank Staff'
                        checked={isBank}
                        onChange={(e) => setIsBank(e.target.checked)}
                        style={textStyle}
                      />
                    </div>

                    {/* Locum */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        marginTop: '1rem',
                      }}
                    >
                      <Form.Check
                        type='checkbox'
                        label={
                          isLocum ? 'Locum Staff,EndDate->' : 'Locum Staff'
                        }
                        checked={isLocum}
                        onChange={(e) => setIsLocum(e.target.checked)}
                        style={textStyle}
                      />
                      {isLocum && (
                        <div>
                          <Form.Control
                            type='date'
                            value={locumEndDate}
                            onChange={(e) => setLocumEndDate(e.target.value)}
                            style={textStyle}
                          />
                        </div>
                      )}
                    </div>

                    {/* Student Nurse */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        marginTop: '1rem',
                      }}
                    >
                      <Form.Check
                        type='checkbox'
                        label='Student Nurse'
                        checked={isStudentNurse}
                        onChange={(e) => setIsStudentNurse(e.target.checked)}
                        style={textStyle}
                      />
                    </div>
                  </Form.Group>

                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Approval Levels
                  </div>
                  <Form.Group className='my-2' controlId='isadmin'>
                    <Form.Check
                      type='checkbox'
                      label='Admin'
                      checked={isAdmin}
                      onChange={(e) => setIsAdmin(e.target.checked)}
                      style={textStyle}
                    />
                  </Form.Group>
                  <Form.Group className='my-2' controlId='ismanager'>
                    <Form.Check
                      type='checkbox'
                      label='Manager'
                      checked={isManager}
                      onChange={(e) => setIsManager(e.target.checked)}
                      style={textStyle}
                    />
                  </Form.Group>
                </div>
              </Col>

              {/* ── Second Column ── */}
              <Col md={6}>
                <div
                  style={{
                    border: '1px solid #ddd',
                    padding: '10px',
                    borderRadius: '10px',
                    backgroundColor: '#F0F8FF',
                  }}
                >
                  {/* Directorate */}
                  <Row>
                    <Col sm={12}>
                      <Form.Group controlId='setDirectorate'>
                        <Form.Label>Directorate</Form.Label>
                        <Form.Control
                          as='select'
                          placeholder='Directorate'
                          value={directoratedepartment}
                          onChange={(e) => {
                            const selectedDirectorate = e.target.value;
                            setDirectoratedepartment(selectedDirectorate);
                            setSpeciality('');
                            setSpecialityID('');
                            setDirectorateID('');
                          }}
                          style={textStyle}
                        >
                          <option value=''>Select Directorate</option>
                          {directorates.map((directorate) => (
                            <option key={directorate} value={directorate}>
                              {directorate}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Ward / Department Area */}
                  <Row className='mt-3'>
                    <Col sm={12}>
                      <Form.Group controlId='wardDepartmentArea'>
                        <Form.Label>Ward / Department Area</Form.Label>
                        <Form.Control
                          as='select'
                          placeholder='Select Ward/Department'
                          value={speciality}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            const selectedIndex = e.target.selectedIndex;
                            const selectedID = e.target.options[
                              selectedIndex
                            ].getAttribute('data-id');
                            const selectedDirectorate = e.target.options[
                              selectedIndex
                            ].getAttribute('data-directorate');

                            setSpeciality(selectedValue);
                            setSpecialityID(selectedID);
                            setDirectorateID(selectedID);

                            if (selectedDirectorate) {
                              setDirectoratedepartment(selectedDirectorate);
                            }
                          }}
                          disabled={!directoratedepartment && !specialityID}
                          style={textStyle}
                        >
                          <option value=''>
                            {directoratedepartment
                              ? 'Select Ward/Department Area'
                              : 'Please select a directorate first'}
                          </option>
                          {directoratedepartment &&
                            locationsByDirectorate[directoratedepartment]?.map(
                              (location) => (
                                <option
                                  key={location._id}
                                  value={location.wardDepartmentArea}
                                  data-id={location._id}
                                  data-directorate={location.directorate}
                                >
                                  {location.wardDepartmentArea}
                                  {location.department &&
                                    ` - ${location.department}`}
                                  {location.costCentreCode &&
                                    ` (${location.costCentreCode})`}
                                </option>
                              )
                            )}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Current location detail */}
                  {currentLocation && (
                    <div className='mt-2 p-2 bg-light rounded'>
                      <small>
                        <strong>Selected Location Details:</strong>
                        <br />
                        Cost Centre: {currentLocation.costCentreCode}
                        <br />
                        Department: {currentLocation.department}
                      </small>
                    </div>
                  )}

                  {/* ── Ward Exclusions ── */}
                  <Row className='mt-3'>
                    <Col sm={12}>
                      <Form.Group controlId='wardExclusions'>
                        <Form.Label className='fw-bold'>
                          Ward Exclusions
                          {wardExclusions.length > 0 && (
                            <span className='ms-2 badge bg-danger'>
                              {wardExclusions.length}
                            </span>
                          )}
                        </Form.Label>
                        <Form.Text className='text-muted d-block mb-2'>
                          Staff cannot be assigned to shifts in these wards.
                          All wards shown regardless of directorate filter.
                        </Form.Text>

                        {/* Dropdown — all wards, sorted by directorate then ward name */}
                        <Form.Control
                          as='select'
                          value=''
                          onChange={(e) => {
                            const selected = e.target.value;
                            if (
                              selected &&
                              !wardExclusions.includes(selected)
                            ) {
                              setWardExclusions([
                                ...wardExclusions,
                                selected,
                              ]);
                            }
                          }}
                          style={textStyle}
                        >
                          <option value=''>
                            Select ward to add to exclusions...
                          </option>
                          {hospitalLocations
                            ?.slice()
                            .sort((a, b) => {
                              const dirCompare = (
                                a.directorate || ''
                              ).localeCompare(b.directorate || '');
                              if (dirCompare !== 0) return dirCompare;
                              return (
                                a.wardDepartmentArea || ''
                              ).localeCompare(b.wardDepartmentArea || '');
                            })
                            .map((location) => (
                              <option
                                key={location._id}
                                value={location.wardDepartmentArea}
                                disabled={wardExclusions.includes(
                                  location.wardDepartmentArea
                                )}
                              >
                                {location.directorate
                                  ? `[${location.directorate}] `
                                  : ''}
                                {location.wardDepartmentArea}
                              </option>
                            ))}
                        </Form.Control>

                        {/* Current exclusions list */}
                        {wardExclusions.length > 0 ? (
                          <div className='mt-2 p-2 border rounded bg-light'>
                            {wardExclusions.map((ward) => (
                              <div
                                key={ward}
                                className='d-flex justify-content-between align-items-center mb-1 p-1 bg-white rounded border'
                              >
                                <small className='text-danger fw-bold'>
                                  ⛔ {ward}
                                </small>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  style={{
                                    padding: '1px 6px',
                                    fontSize: '0.7rem',
                                  }}
                                  onClick={() =>
                                    setWardExclusions(
                                      wardExclusions.filter((w) => w !== ward)
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className='mt-2 p-2 border rounded bg-light'>
                            <small className='text-muted fst-italic'>
                              No ward exclusions set
                            </small>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Opening Balance and Bring Forward */}
                  <Row className='mt-3'>
                    <Col sm={6}>
                      <Form.Group controlId='openingBalance'>
                        <Form.Label>
                          AL Balance (Hours-auto calculated)
                        </Form.Label>
                        <Form.Control
                          type='number'
                          step='0.10'
                          placeholder='Enter Opening Balance'
                          value={openingBalance}
                          onChange={(e) =>
                            setOpeningBalance(Number(e.target.value))
                          }
                          style={textStyle}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group controlId='bringForward'>
                        <Form.Label>
                          AL Balance - Bring Forward (Hours)
                        </Form.Label>
                        <Form.Control
                          type='number'
                          step='0.10'
                          placeholder='Enter Bring Forward'
                          value={bringForward}
                          onChange={(e) =>
                            setBringForward(Number(e.target.value))
                          }
                          style={textStyle}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Comments */}
                  <Form.Group className='my-2' controlId='comment'>
                    <Form.Label>Comments</Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={3}
                      placeholder='Comments'
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      style={textStyle}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>

            <Button type='submit' variant='primary' className='mt-3'>
              Update
            </Button>
          </Form>
        )}
      </Container>
    </>
  );
};

export default UserEditScreen;
